import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col, Button } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";

const SummaryReport = () => {
    const { adminUser } = useAdminAuth();
    const [reportData, setReportData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fundingAgencies, setFundingAgencies] = useState([]);

    const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
    const [currentPage, setCurrentPage] = useState(1);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Filters
    const [selectedFundingAgency, setSelectedFundingAgency] = useState(null);
    const [selectedLoanType, setSelectedLoanType] = useState(null);
    const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const itemsPerPage = 20;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const OpenSidebar = () => {
        setOpenSidebarToggle(!openSidebarToggle);
    };

    // Loan Type Options
    const loanTypeOptions = [
        { value: "All", label: "All Loan Types" },
        { value: "Business Loan", label: "Business Loan" },
        { value: "JLG Loan", label: "JLG Loan" },
        { value: "GP Loan", label: "GP Loan" },
    ];

    // Fetch funding agencies
    useEffect(() => {
        const fetchFundingAgencies = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/getModelData/funding_agencies`);
                if (response.data && response.data.data) {
                    const agencies = response.data.data.map((agency) => ({
                        value: agency.id,
                        label: agency.agencyName,
                    }));
                    setFundingAgencies([{ value: "All", label: "All Funding Agencies" }, ...agencies]);
                }
            } catch (error) {
                console.log("Error fetching funding agencies:", error);
            }
        };
        fetchFundingAgencies();
    }, []);

    // Fetch report data
    const fetchReportData = async () => {
        setLoading(true);
        try {
            const params = {
                id: adminUser.user.id,
                loanType: selectedLoanType?.value || "All",
                fundingAgencyId: selectedFundingAgency?.value || "All",
            };

            const response = await axios.get(
                `${BASE_URL}/api/getSummaryReportData`,
                { params }
            );

            if (response.data) {
                setReportData(response.data);
                setFilteredData(response.data);
            } else {
                console.log("Error in fetching Data");
            }
        } catch (error) {
            console.log("Error fetching Data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on initial load
    useEffect(() => {
        fetchReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminUser.user.id]);

    const [branches, setBranches] = useState([]);

    // Fetch branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/getall/branches`);
                if (response.data) {
                    const branchOptionsData = response.data.map((branch) => ({
                        value: branch.branchName,
                        label: branch.branchName,
                    }));
                    setBranches([{ value: "All", label: "All Branches" }, ...branchOptionsData]);
                }
            } catch (error) {
                console.log("Error fetching branches:", error);
            }
        };
        fetchBranches();
    }, []);

    // Branch options for dropdown
    const branchOptions = branches;

    // Fetch report data when backend filters change
    useEffect(() => {
        fetchReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLoanType, selectedFundingAgency]);

    // Apply filters when any filter changes
    useEffect(() => {
        let filtered = [...reportData];

        // Branch filter (frontend filter)
        if (selectedBranchFilter && selectedBranchFilter.value !== "All") {
            filtered = filtered.filter(
                (item) => item.branchName === selectedBranchFilter.value
            );
        }

        if (searchTerm && searchTerm.trim() !== "") {
            const term = searchTerm.trim().toLowerCase();
            filtered = filtered.filter((item) => {
                const memberName = String(item?.memberName || "").toLowerCase();
                const customerId = String(item?.customerId || "").toLowerCase();
                const loanId = String(item?.loanId || "").toLowerCase();
                const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
                const aadharNo = String(item?.aadharNo || "").toLowerCase();
                const applicationId = String(item?.ApplicationId || "").toLowerCase();

                return (
                    memberName.includes(term) ||
                    customerId.includes(term) ||
                    loanId.includes(term) ||
                    phoneNumber.includes(term) ||
                    aadharNo.includes(term) ||
                    applicationId.includes(term)
                );
            });
        }

        setFilteredData(filtered);
        setCurrentPage(1);
    }, [reportData, selectedBranchFilter, searchTerm]);

    // Calculate totals
    const calculateTotals = () => {
        const totalLoanAmount = filteredData.reduce(
            (sum, item) => sum + (Number(item.proposedLoanDetails?.loanAmount) || 0),
            0
        );
        const totalMembers = filteredData.length;
        const totalProcessingFee = filteredData.reduce(
            (sum, item) => sum + (Number(item.processingCharge) || 0),
            0
        );
        const totalGst = filteredData.reduce(
            (sum, item) => sum + (Number(item.gst) || 0),
            0
        );
        const totalSecurityDeposit = filteredData.reduce(
            (sum, item) => sum + (Number(item.securityDeposit) || 0),
            0
        );

        return {
            totalLoanAmount,
            totalMembers,
            totalProcessingFee,
            totalGst,
            totalSecurityDeposit,
        };
    };

    const totals = calculateTotals();

    // Define columns with label and render logic
    const columns = [
        { label: "S No", render: (data, index) => indexOfFirstItem + index + 1 },
        { label: "Application ID", render: (data) => data.ApplicationId || "N/A" },
        { label: "Member Name", render: (data) => data.memberName || "N/A" },
        { label: "Loan Type", render: (data) => data.loanType || "N/A" },
        { label: "Funding Agency", render: (data) => data.fundingAgencyName || "N/A" },
        { label: "Branch", render: (data) => data.branchName || "N/A" },
        { label: "Region", render: (data) => data.regionName || "N/A" },
        {
            label: "Loan Amount",
            render: (data) =>
                Number(data.proposedLoanDetails?.loanAmount || 0).toLocaleString("en-IN"),
        },
        {
            label: "Rate of Interest",
            render: (data) =>
                Number(data.proposedLoanDetails?.rateOfInterest || 0).toFixed(2),
        },
        {
            label: "Processing Fee",
            render: (data) =>
                Number(data.processingCharge || 0).toLocaleString("en-IN"),
        },
        {
            label: "GST",
            render: (data) => Number(data.gst || 0).toLocaleString("en-IN"),
        },
        {
            label: "Security Deposit",
            render: (data) =>
                Number(data.securityDeposit || 0).toLocaleString("en-IN"),
        },
        { label: "Status", render: (data) => data.branchManagerStatus || "N/A" },
    ];

    return (
        <section className="master-layout">
            <div className="master-sidebar-wrapper">
                <Sidebar
                    openSidebarToggle={openSidebarToggle}
                    OpenSidebar={OpenSidebar}
                />
            </div>

            <div className="master-content-wrapper">
                <div className="master-header-wrapper">
                    <DashboardHeader OpenSidebar={OpenSidebar} />
                </div>

                <div className="master-card-container">
                    <div className="master-search-section">
                        <h1 className="TableHeaderText w-100 text-start">Summary Report</h1>

                        <button
                            className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                        >
                            <span style={{ fontWeight: 600, fontSize: "14px" }}>
                                {showMobileFilters ? "Hide Filters" : "Show Filters"}
                            </span>
                            <MdExpandMore size={22} />
                        </button>

                        <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
                            <Row>
                                <Col xs={12} lg={3} md={6} className="mb-2">
                                    <Form.Group>
                                        <Form.Label>Search Customer</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} lg={3} md={6}>
                                    <Form.Label>Funding Agency</Form.Label>
                                    <Select
                                        value={selectedFundingAgency}
                                        onChange={(val) => {
                                            setSelectedFundingAgency(val);
                                            if (val && selectedLoanType && selectedBranchFilter) {
                                                setTimeout(() => setShowMobileFilters(false), 300);
                                            }
                                        }}
                                        options={fundingAgencies}
                                        isClearable
                                        placeholder="Select Funding Agency"
                                        className="filter-dropdown"
                                    />
                                </Col>
                                <Col xs={12} lg={3} md={6}>
                                    <Form.Label>Product/Loan Type</Form.Label>
                                    <Select
                                        value={selectedLoanType}
                                        onChange={(val) => {
                                            setSelectedLoanType(val);
                                            if (selectedFundingAgency && val && selectedBranchFilter) {
                                                setTimeout(() => setShowMobileFilters(false), 300);
                                            }
                                        }}
                                        options={loanTypeOptions}
                                        isClearable
                                        placeholder="Select Loan Type"
                                        className="filter-dropdown"
                                    />
                                </Col>
                                <Col xs={12} lg={3} md={6}>
                                    <Form.Label>Branch</Form.Label>
                                    <Select
                                        value={selectedBranchFilter}
                                        onChange={(val) => {
                                            setSelectedBranchFilter(val);
                                            if (selectedFundingAgency && selectedLoanType && val) {
                                                setTimeout(() => setShowMobileFilters(false), 300);
                                            }
                                        }}
                                        options={branchOptions}
                                        isClearable
                                        placeholder="Filter by Branch"
                                        className="filter-dropdown"
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <div className="master-list-section">
                        <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
                            <DataTable
                                columns={columns}
                                data={paginationData}
                                reportName="SummaryReport"
                                excelData={filteredData}
                            />
                        </div>

                        <div className="master-pagination-wrapper">
                            <CustomPagination
                                totalPages={totalPages}
                                currentPage={currentPage}
                                paginate={paginate}
                                totalItems={filteredData.length}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SummaryReport;
